import { useMemo } from "react";
import { useRtkQueryDynamicEndpoint } from "../../../../../common/hooks/useRtkQueryDynamicEndpoint";
import { useBuildCurtailmentObjectsMutation } from "../../../../../requests_cm/gecoSitesService/service";
import { If } from "../../../../../common/components/If";
import ErrorMessageModal from "../../../../../common/components/ErrorMessageDialog";
import {
	ErrorType,
	formatApiErrorMessage,
} from "../../../../../common/utils/formatApiErrorMessage";
import { GenerateCurtailmentObjectsFromSite } from "../../../../../common/BuildCurtailmentObjectsComponents/GenerateCurtailmentObjectsFromSite";
import { GeneratingCurtailmentNothingModal } from "../../../../../common/BuildCurtailmentObjectsComponents/GeneratingCurtailmentNothingModal";
import { GeneratingCurtailmentObjectIncompleteModal } from "../../../../../common/BuildCurtailmentObjectsComponents/GeneratingCurtailmentObjectIncompleteModal";
import { GeneratingCurtailmentSuccessModal } from "../../../../../common/BuildCurtailmentObjectsComponents/GeneratingCurtailmentSuccessModal";
import { GeneratingCurtailmentObjectFailedModal } from "../../../../../common/BuildCurtailmentObjectsComponents/GeneratingCurtailmentObjectFailedModal";
import { GeneratingCurtailmentLoadingModal } from "../../../../../common/BuildCurtailmentObjectsComponents/GeneratingCurtailmentLoadingModal";

export enum BuildStatuses {
	initial = "initial",
	success = "success",
	loading = "loading",
	empty = "empty",
	failed = "failed",
	incomplete = "incomplete",
}

export interface BuildCurtailmentObjectsModalContainerProps {
	siteId: number;
	isOpen: boolean;
	onClose: () => void;
}
export const BuildCurtailmentObjectsModalContainer = ({
	siteId,
	isOpen,
	onClose,
}: BuildCurtailmentObjectsModalContainerProps) => {
	const [baseBuildCurtailmentObjects, { data, error, isLoading, reset }] =
		useBuildCurtailmentObjectsMutation();
	const buildCurtailmentObjects = useRtkQueryDynamicEndpoint(
		baseBuildCurtailmentObjects
	);
	const currentStatus = useMemo(() => {
		if (isLoading) {
			return BuildStatuses.loading;
		}
		if (data && data.errors.length && data.success.length) {
			return BuildStatuses.incomplete;
		}
		if (data && !data.errors.length && data.success.length) {
			return BuildStatuses.success;
		}
		if (data && data.errors.length && !data.success.length) {
			return BuildStatuses.failed;
		}
		if (data && !data.errors.length && !data.success.length) {
			return BuildStatuses.empty;
		}
		return BuildStatuses.initial;
	}, [data, isLoading]);

	const onCloseModal = () => {
		onClose();
		reset();
	};

	return (
		<>
			<If condition={isOpen}>
				<GenerateCurtailmentObjectsFromSite
					isOpen={currentStatus === BuildStatuses.initial}
					onClose={onCloseModal}
					onSubmit={({
						startDate,
						endDate,
						create_ch51,
                        fix_overlapping_horizons
					}: {
						startDate: string;
						endDate: string;
						create_ch51: string;
                        fix_overlapping_horizons: string;
					}) =>
						buildCurtailmentObjects({ startDate, endDate, create_ch51,fix_overlapping_horizons, siteId })
					}
				/>
				<GeneratingCurtailmentLoadingModal
					isOpen={currentStatus === BuildStatuses.loading}
				/>
				<GeneratingCurtailmentNothingModal
					isOpen={currentStatus === BuildStatuses.empty}
					onClose={onCloseModal}
				/>
				<GeneratingCurtailmentSuccessModal
					isOpen={currentStatus === BuildStatuses.success}
					onClose={onCloseModal}
				/>
				<GeneratingCurtailmentObjectFailedModal
					errors={data?.errors || []}
					success={data?.success || []}
					isOpen={currentStatus === BuildStatuses.failed}
					onClose={onCloseModal}
				/>
				<GeneratingCurtailmentObjectIncompleteModal
					errors={data?.errors || []}
					success={data?.success || []}
					isOpen={currentStatus === BuildStatuses.incomplete}
					onClose={onCloseModal}
				/>
			</If>
			<ErrorMessageModal
				title={"Failed to generate curtailment object"}
				content={formatApiErrorMessage(error as ErrorType)}
				actionAfterClose={reset}
			/>
		</>
	);
};
